import React, { forwardRef } from 'react';
import {
  Box,
  GDSCustomizableComponent,
  GDSSpace,
  GDSStyleObject,
  GDSBreakpoint,
  queryHelpers,
} from '@leagueplatform/genesis-core';

export type GDSSidebarSizes = 'md' | 'lg';
export type GDSSidebarSides = 'leading' | 'trailing';

type GDSSidebarWidths = {
  [K in GDSSidebarSizes]: string;
};

const sidebarWidths: GDSSidebarWidths = {
  md: '288px',
  lg: '384px',
};

export type GDSSidebarLayoutProps = {
  mainMinWidth?: string;
  sidebarSide?: GDSSidebarSides;
  sidebarSize?: GDSSidebarSizes;
  spacing?: GDSSpace;
  stretch?: boolean;
  verticalBreakpoint?: GDSBreakpoint;
} & GDSCustomizableComponent;

/**
 * Component for positioning two sub-content sections into a sidebar and main content layout.
 *
 * @component
 * @property {string} [mainMinWidth='50%'] - Minimum width of the main content area. Most instances should never set this property. Defaults to '50%'.
 * @property {'leading' | 'trailing'} [sidebarSide='leading'] - Position of the sidebar relative to the main content. Defaults to 'trailing'.
 * @property {SidebarSizes} [sidebarSize='md'] - Size of the sidebar. Defaults to 'md'.
 * @property {GDSSpace} [spacing='$two'] - Spacing between the sidebar and main content. Defaults to '$two'.
 * @property {boolean} [stretch=false] - Whether the subsections should stretch to fill the available height. Defaults to false.
 * @property {GDSBreakpoint} [verticalBreakpoint='tablet'] - Breakpoint at which the sidebar switches from horizontal to vertical layout. Defaults to 'md'.
 *
 * @example
 * <SidebarLayout sidebarSide="leading"><nav>...</nav><section>...</section></SidebarLayout>
 */

export const SidebarLayout = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<GDSSidebarLayoutProps>
>(
  (
    {
      children,
      css,
      mainMinWidth = '50%',
      sidebarSide = 'trailing',
      sidebarSize = 'md',
      spacing = '$two',
      stretch,
      verticalBreakpoint = 'tablet',
      ...props
    },
    ref,
  ) => {
    const isSidebarTrailing = sidebarSide === 'trailing';
    const width = sidebarWidths[sidebarSize] ?? sidebarSize;
    // TODO: Currently queryHelpers does not handle
    // if a non-gds value is passed. Update in the future to
    // handle or return undefined.
    const breakpoint = React.useMemo(
      () => queryHelpers.down(verticalBreakpoint),
      [verticalBreakpoint],
    );
    const sidebarStyles: GDSStyleObject = React.useMemo(
      () => ({
        flexGrow: 1,
        width,
        flexBasis: width,
        [`@media ${breakpoint}`]: {
          width: 'auto',
          flexBasis: 'auto',
        },
      }),
      [breakpoint, width],
    );
    const mainContentStyles: GDSStyleObject = React.useMemo(
      () => ({
        flexBasis: 0,
        flexGrow: 999,
        minInlineSize: mainMinWidth,
      }),
      [mainMinWidth],
    );

    return (
      <Box
        ref={ref}
        className="GDS-sidebar-layout"
        css={{
          gap: spacing,
          display: 'flex',
          alignItems: stretch ? 'stretch' : 'flex-start',
          [`@media ${breakpoint}`]: {
            flexDirection: 'column',
            alignItems: 'initial',
          },

          '& > :first-child': isSidebarTrailing
            ? mainContentStyles
            : sidebarStyles,

          '& > :last-child': isSidebarTrailing
            ? sidebarStyles
            : mainContentStyles,
          ...css,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

SidebarLayout.displayName = 'SidebarLayout';
